import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useGlobalState } from '../../../../stateProvider/StateProvider';
import { QuestRoute } from '../../../../api/QuestRoutes';
import { beginningOfMonth, exportToPDF, formatCurrency, generateId, inputDate, offsetUTC, planeDate, translate } from '../../../../utility/functions';
import Page from '../../../../layout/Page';
import Button from '../../../../forms/Button';
import Row from '../../../../layout/Row';
import Card from '../../../../forms/Card';
import Col from '../../../../layout/Col';
import DateRange from '../../../../table/DateRange';
import Heading from '../../../../forms/Heading';
import Loading from '../../../../Loading';
import Select from '../../../../forms/Select';
import DailyDrop from './DailyDrop';
import RedeemedDrop from './RedeemedDrop'
import Table from '../../../../table/Table';
import { SCREEN_WIDTH } from '../../../../../constants/config';
import CompanyLogo from '../../../../CompanyLogo';

const HeaderRow = styled.div`
    display: flex;
    gap: 4em;
    justify-content: space-between;
    align-items: flex-start;
`
const ReportRow = styled(Row)`
    align-items: flex-start;
    justify-content: space-between;
    width: 100%;
    gap: 7em;
    border-bottom: 1px solid ${({ theme }) => theme.gray}40;
`
const Cell = styled.span`
    flex: 1;

    &:first-child {
        flex: 2;
    }
`
const Report = styled(Card)`
    width: -webkit-fill-available;
    width: -moz-available;
`
const ReportTable = styled(Table)`
    tbody tr {
        background: ${({ bColor }) => bColor}1a;
        border-width: 3px;
    }
    tbody tr:hover {
        background: ${({ bColor }) => bColor}1a;
    }
    thead th {
        background: ${({ bColor }) => bColor}40;
    }
    tbody tr:first-child {
        border-top: unset;
    }
    // thead th:first-child {
    //     border-top-left-radius: 2em;
    //     border-bottom-left-radius: 2em;
    // }
    // thead th:last-child {
    //     border-top-right-radius: 2em;
    //     border-bottom-right-radius: 2em;
    // }
`

const Daily = ({ ...props }) => {
    const { state: { allLocations } } = useGlobalState();

    const cardRef = useRef();

    const [dataSet, setDataSet] = useState(null);
    const [data, setData] = useState(null);
    const [redeemed, setRedeemed] = useState(null);
    const [location, setLocation] = useState('');
    const [dateRange, setDateRange] = useState({startDate: beginningOfMonth, endDate: inputDate()});

    const sortIndex = [
        'Wash',
        'AppWashClubBilling',
        'LPR',
        'AppWashClub',
        'WashClubSignUp',
        'AppWashClubSignUp',
        'WashClubChange',
        'WashClubReactivation',
        'AppWash',
        'PrepaidPassBilling',
        'CodeBundle',
        'BuyUp',
        'TokenCoin',
        'ValueCardIssue',
        'Time',
        'Parking',
        'Fee',
        'Redeemed',
    ]

    const itemColors = [
        '#4BC0C0',
        '#36A2EB',
        '#FF6384',
        '#FFCE56',
        '#9966FF',
        '#FF9F40',
        '#8DFF9F',
        '#FF85DD',
        '#55D9FF',
        '#FF7373',
        '#FFD173',
        '#A073FF',
        '#73FFA1',
        '#FFA773',
        '#FFEA73',
        '#737FFF',
        '#FFB2D9',
        '#AFFFD7'
    ];

    const buildDataSet = (items, setItems) => {
        const dailyStats = initializeSet(items)

        Object.values(dailyStats).forEach((e) => {
            const { items } = e;

            e.totalCount = items.reduce((a, b) => a + b.quantity, 0);

            const nameCount = items.reduce((countMap, item) => {
                const name = item.name;
                countMap[name] = (countMap[name] || 0) + 1;
                return countMap;
            }, {});

            items.sort((a, b) => nameCount[b.name] - nameCount[a.name]);

            e.itemized = Object.entries(items.reduce((acc, transaction) => {
                const { name, price, quantity, location, discount, discountType } = transaction;

                if (!acc[name]) acc[name] = [];

                const existingItem = acc[name].find(item => item.price === price && item.discount === discount);

                if (existingItem) {
                    existingItem.quantity += quantity;
                    const existingLocation = existingItem.locations.find(loc => loc.location === location);
                    if (existingLocation) {
                        existingLocation.quantity += quantity;
                    } else {
                        existingItem.locations.push({ location, quantity });
                    }
                } else {
                    acc[name].push({ price, quantity, discount, discountType, locations: [{ location, quantity }] });
                }

                acc[name].sort((a, b) => b.quantity - a.quantity);

                return acc;
            }, {}));

            e.discountTotal = items.reduce((a, b) => a + (b.discount ?? 0), 0);
            e.subTotal = items.reduce((a, b) => a + (b.quantity * b.price), 0);
            e.total = items.reduce((a, b) => a + (Math.max(0, (b.price - (b.discount ?? 0))) * b.quantity), 0);
        })

        setItems(dailyStats);
    }

    const buildRedemptionSet = (items, setItems) => {
        const redemptionStats = initializeSet(items)

        Object.values(redemptionStats).forEach((e) => {
            const { items } = e;
            
            //Create total count
            e.totalCount = items.reduce((a, b) => a + b.quantity, 0);

            //Create Counts by Item Name
            const nameCount = items.reduce((countMap, item) => {
                const name = item.name;
                countMap[name] = (countMap[name] || 0) + 1;
                return countMap;
            }, {});

            //Sort object based on Name Count (highest at top)
            items.sort((a, b) => nameCount[b.name] - nameCount[a.name]);

            e.itemized = Object.entries(items.reduce((acc, transaction) => {
                const { name, price, quantity, location, discount, discountType } = transaction;

                if (!acc[name]) acc[name] = [];

                const existingItem = acc[name].find(item => item.price === price && item.discount === discount && item.discountType === discountType);

                if (existingItem) {
                    existingItem.quantity += quantity;
                    const existingLocation = existingItem.locations.find(loc => loc.location === location && loc.discountType === discountType);
                    if (existingLocation) {
                        existingLocation.quantity += quantity;
                    } else {
                        existingItem.locations.push({ location, quantity, discountType });
                    }
                } else {
                    acc[name].push({ price, quantity, discount, discountType, locations: [{ location, discountType, quantity }] });
                }
                acc[name].sort((a, b) => b.quantity - a.quantity);
                return acc;
            }, {}));

        })
        setItems(redemptionStats);
    }

    const initializeSet = (items) => {
        const set = {};

        if (location) {
            items = items.filter(e => e.locationId === location);
        }

        items.forEach(e => {
            if (set[e.itemType]) {
                const { items, locations, breakdown } = set[e.itemType];
                items.push(e);
                locations[e.location] ? locations[e.location]++ : locations[e.location] = 1;
                breakdown[e.itemId] ? breakdown[e.itemId].push(e) : breakdown[e.itemId] = [e];
            } else {
                set[e.itemType] = {
                    items: [e],
                    locations: {
                        [e.location]: 1
                    },
                    breakdown: {
                        [e.itemId]: [e]
                    }
                }
            }
        })

        return set;
    }

    useEffect(() => {
        if (!dataSet) return;
        dataSet.forEach(e => {
            if (e.itemType === 'BuyUp') {
                e.discount = 0;
            };
            if (e.itemType === 'WashClubReactivation') {
                e.flags = null;
            };
            if (e.flags === 'Loyalty' && !e.discountType) {
                const newName = e.name.split(',');
                e.name = newName[newName.length - 1];
                e.flags = null;
            };
        })

        buildDataSet(dataSet.filter(e => !e.flags), setData)
        buildRedemptionSet(dataSet.filter(e => e.flags), setRedeemed)
    }, [dataSet, location])

    useEffect(() => {
        // if (!data) return;
        //console.log(redeemed)
    }, [redeemed, data])

    return (
        <Page>

            <Row wrap>
                <Select
                    label='Location'
                    options={[{ value: '', label: 'All Locations' }, ...allLocations]}
                    value={location}
                    onChange={(e) => setLocation(parseInt(e.target.value))}
                    standalone
                    style={{ minWidth: '10em' }}
                />
                <DateRange
                    route={QuestRoute.reports.daily}
                    setData={setDataSet}
                    onDateRangeChange={({ startDate, endDate }) => {setDateRange({ startDate, endDate });}}
                    searchLimit={300}
                    monthStart={false}
                />
            </Row>

            <Button
                label='Export'
                disabled={!data}
                onClick={() => exportToPDF(cardRef, 'Sales Summary')}
            />

            <Report title='Sales Summary' icon='clipboard'>
                <div ref={cardRef}>
                    {data ?
                        (Object.entries(data).length || Object.entries(redeemed).length) ?
                            <>
                                <HeaderRow style={{ marginBottom: '1em' }}>
                                    <Col>
                                        <Heading>Period</Heading>
                                        <HeaderRow>
                                            <span>Start: {planeDate(offsetUTC(dateRange.startDate))}</span>
                                        </HeaderRow>
                                        <HeaderRow>
                                            <span>End: {planeDate(offsetUTC(dateRange.endDate))}</span>
                                        </HeaderRow>
                                        {(() => { 
                                            const locLabel = allLocations.find(l => l.value === location)?.label;
                                            return locLabel ? ( 
                                                <HeaderRow style={{ marginTop: '0.5em' }}>
                                                    <span><b>Location:</b> {locLabel}</span> 
                                                </HeaderRow>
                                                
                                            ) : null; }
                                        )()}                                     
                                    </Col>
                                    <CompanyLogo dim={6} />
                                </HeaderRow>                               
                                <Col style={{ gap: '5em' }}>
                                    <Col>
                                        <Heading>Sales</Heading>
                                        <ReportRow style={{ background: '#6fcc6c40', padding: '0.3em 0' }}>
                                            {SCREEN_WIDTH ?
                                                <Cell>
                                                    <b style={{ padding: '0 0.3em' }}>GROSS REVENUE: {formatCurrency(Object.values(data).reduce((a, b) => a + b.total, 0))}</b>
                                                </Cell> :
                                                <>
                                                    <Cell>
                                                        <b style={{ padding: '0 0.3em' }}>GROSS REVENUE</b>
                                                    </Cell>
                                                    <Cell />
                                                    <Cell>
                                                        <b>{formatCurrency(Object.values(data).reduce((a, b) => a + b.total, 0))}</b>
                                                    </Cell>
                                                </>
                                            }
                                        </ReportRow>

                                        {Object.entries(data)
                                            .sort((a, b) => sortIndex.indexOf(a[0]) - sortIndex.indexOf(b[0]))
                                            .map(([key, val], i) => {
                                                const heading = key;
                                                const { itemized, total, totalCount } = val;

                                                return (
                                                    <ReportTable
                                                        // key={`salesTable-${i}`}
                                                        key={generateId()}
                                                        bColor={itemColors[i % itemColors.length]}
                                                        config={{
                                                            colSpacing: [30, 20, 30, 20],
                                                            disableTableControls: true,
                                                            disableHeaderSearch: true,
                                                            disableAnimation: true,
                                                            data: itemized,
                                                            headers: [{
                                                                label: translate(heading),
                                                            }, {
                                                                label: 'Quantity: ' + totalCount,
                                                            }, {
                                                                label: 'Price',
                                                            }, {
                                                                label: 'Sales: ' + formatCurrency(total),
                                                            }],
                                                            renderFormat: (e) => (
                                                                <tr key={generateId()}>
                                                                    <DailyDrop
                                                                        label={e[0]}
                                                                        quantity={e[1].map(i => i.quantity)}
                                                                        price={e[1].map(i => formatCurrency(Math.max(0, i.price - (i.discount ?? 0))))}
                                                                        discount={e[1].map(i => i.discount)}
                                                                        discountType={e[1].map(i => i.discountType)}
                                                                        locations={e[1].map(i => i.locations)}
                                                                    />
                                                                    <td>{formatCurrency(e[1].reduce((a, b) => a + (Math.max(0, (b.price - (b.discount ?? 0))) * b.quantity), 0))}</td>
                                                                </tr>
                                                            )
                                                        }}
                                                    />
                                                )
                                            })}
                                    </Col>

                                    {Object.entries(redeemed).length ?
                                        <Col>
                                            <Heading>Redemptions</Heading>
                                            {Object.entries(redeemed).map(([key, val], i) => {
                                                const heading = key;
                                                const { itemized, total, totalCount } = val;
                                                
                                                return (
                                                    <ReportTable
                                                        key={`reportTable-${i}`}
                                                        bColor={itemColors[i]}
                                                        config={{
                                                            colSpacing: [30, 20, 30, 20],
                                                            disableTableControls: true,
                                                            disableHeaderSearch: true,
                                                            disableAnimation: true,
                                                            data: itemized,
                                                            headers: [{
                                                                label: translate(heading),
                                                            }, {
                                                                label: 'Quantity: ' + totalCount,
                                                            }],
                                                            renderFormat: (e) => (
                                                                <tr key={generateId()}>
                                                                    <RedeemedDrop
                                                                        label={e[0]}
                                                                        quantity={e[1].map(i => i.quantity)}
                                                                        breakdown={e[1].map(i => i.locations)}
                                                                    />
                                                                </tr>
                                                            )
                                                        }}
                                                    />
                                                )
                                            })}
                                            {/* <ReportRow>
                                                <Cell>
                                                    <b>GROSS REDEEMED</b>
                                                </Cell>
                                                <Cell></Cell>
                                                <Cell style={{ textAlign: 'right' }}>
                                                    <b>{formatCurrency(Object.values(redeemed).reduce((a, b) => a + b.total, 0))}</b>
                                                </Cell>
                                            </ReportRow> */}
                                        </Col> : null}

                                </Col>                           
                            </>                           
                            : <>Nothing to Report</>
                        : <Loading />
                    }
                </div>
            </Report>
        </Page >
    );
}

export default Daily;